/**
 * Imports the necessary components and libraries for a React form.
 * @import React from "react" - The React library.
 * @import { ErrorMessage } from "@hookform/error-message" - The ErrorMessage component from the "@hookform/error-message" library.
 * @import { Controller } from "react-hook-form" - The Controller component from the "react-hook-form" library.
 * @import { Form } from "react-bootstrap" - The Form component from the "react-bootstrap" library.
 */
import React from "react";
import { ErrorMessage } from "@hookform/error-message";
import { Controller } from "react-hook-form";
import { Form } from "react-bootstrap";

/**
 * A custom text field component that renders a form control with error handling and validation.
 * @param {Object} props - The props object containing various configuration options for the text field.
 * @param {Object} props.errors - The errors object containing validation errors for the field.
 * @param {string} props.placeholder - The placeholder text for the text field.
 * @param {Array} props.multiErrorFields - An array of objects representing multiple error fields.
 * @param {boolean} props.disabled - Whether the text field is disabled or not.
 * @param {string} props.type - The type of the input field.
 * @param {boolean} props.textarea - Whether the input field should be a textarea or not.
 *
 */
const TextField = ({
  errors = {},
  placeholder,
  multiErrorFields = [],
  disabled,
  type,
  textarea,
  name,
  autoFocus = false,
  handleFocus,
  handleBlur,
  maxLength,
  minLength,
  onKeyDown,
  formMethod,
  rows,
  defaultValue,
  rules,
  errorIcon = "icon-error_outline",
  successIcon = "icon-checkmark",
  readOnly = false,
  required = true,
  min,
  max,
  onClick,
  ref,
  textCountVisible,
  textcount,
  textCountTotal,
  ...props
}) => {

  const hasError = errors[name] !== undefined;

  return (
    <>
      <Controller
        defaultValue={defaultValue}
        render={({ field: { onChange, value } }) => (
          <>
            <Form.Control
              className={hasError ? "is-invalid" : ""}
              name={name}
              ref={ref}
              placeholder={placeholder}
              autoFocus={autoFocus}
              disabled={disabled}
              autoComplete="off"
              required={required}
              type={type}
              as={textarea}
              readOnly={readOnly}
              rows={rows}
              onBlur={() => handleBlur}
              onKeyDown={onKeyDown}
              maxLength={maxLength}
              minLength={minLength}
              min={min}
              max={max}
              onFocus={(e) => {
                handleFocus && handleFocus(e);
              }}
              defaultValue={defaultValue}
              onChange={(e) => {
                onChange && onChange(e.target.value.trimLeft());
                props.onChange && props.onChange(e);
              }}
              onClick={onClick}
              value={formMethod.watch(name) || value ? value : ""}
            />
          </>
        )}
        name={name}
        control={formMethod?.control}
        rules={rules}
      />
      {textCountVisible && (
        <span className="badgecount badge bg-success">
          {textcount} / {textCountTotal}
        </span>
      )}
      {multiErrorFields?.length > 0 ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ messages }) => {
            if (messages) {
              const isMultipleError = Object.keys(messages).every(
                (errKey) =>
                  multiErrorFields.filter((m) => m[errKey] !== undefined)
                    .length > 0
              );
              if (isMultipleError) {
                const arr = [];
                for (const fieldError of multiErrorFields) {
                  const key = Object.keys(fieldError)[0];
                  const success = Object.keys(messages).includes(key);
                  arr.push(
                    <div className={success ? "red" : "green"} key={key}>
                      <span key={key}>
                        <i className={success ? errorIcon : successIcon} />
                        {fieldError[key]}
                      </span>
                    </div>
                  );
                }
                return (
                  <div className="errorMsg 2 show passwordcustom">{arr}</div>
                );
              } else {
                return (
                  <div className="errorMsg 1 show">
                    <i className={errorIcon} />
                    {errors[name]?.message}
                  </div>
                );
              }
            } else {
              return <div className="errorMsg 3"></div>;
            }
          }}
        />
      ) : hasError ? (
        <div className="errorMsg 4 show">
          <span>
            {/**
             * Default props for the TextField component.
             * @type {object}
             * @property {boolean} autoFocus - Whether the text field should be automatically focused.
             * @property {string} value - The initial value of the text field.
             * @property {object} errors - An object containing error messages for the text field.
             * @property {Array} multiErrorFields - An array of field names that have multiple errors.
             * @property {boolean} noNegative - Whether negative values are allowed in the text field.
             * @property {string} errorIcon - The icon to display for error messages.
             * @property {string} successIcon - The icon to display for success messages.
             * @property {boolean} readOnly - Whether the text
             */}
            <i className={errorIcon} />
            {errors[name]?.message}
          </span>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

export default TextField;
